import { useTranslation } from 'react-i18next';
import { LaborLogo } from '@/shared/components/LaborLogo';
import { useTenant } from '@/app/providers/TenantContextProvider';
import { useCurrentPartnerLab } from '@/shared/hooks/useCurrentPartnerLab';
import { usePrincipal } from '@/core/auth/appToken';
import { SUPPORT_DASHBOARD_ALLOWED_ROLES } from '@/models/role';
import type { OrganizationType } from '@/models/organizationType';

/*
 * The `SUPPORT_DASHBOARD_ALLOWED_ROLES` exclusion described below is enforced inside
 * `useCurrentPartnerLab` itself now (shared cache entry, single enforcement point for every
 * consumer) — this component only recomputes it to decide its own render output, it no longer
 * needs to gate the request via `enabled`.
 */

/**
 * Organisation types that see their own partner laboratory's contact data (mirrors the gating
 * in {@link ../layout/UserMenu.UserMenu | UserMenu}'s "Meine Organisation" entry).
 */
const CUSTOMER_ORG_TYPES: ReadonlySet<OrganizationType> = new Set([
  'PRACTICE',
  'CUSTOMER_LAB',
]);

/**
 * Header block showing the active tenant's partner laboratory (logo, name, phone, city). Without a
 * logo, or when it fails to load, a neutral placeholder icon is shown instead.
 *
 * Renders only for customer org contexts ({@link CUSTOMER_ORG_TYPES}); DELABO/support contexts
 * render nothing (E01-03 AK-6). Data comes from {@link useCurrentPartnerLab}, which resolves the
 * mandant-scoped partner lab from `GET /api/v1/partner-lab/current` — the backend derives the
 * owning organization solely from the caller's token, this component never sends a tenant id.
 * While loading, or while no partner lab is assigned yet (onboarding pending, backend `204`),
 * nothing is rendered — never an empty box or an error state (AK-5).
 *
 * Defense in depth: also explicitly hidden for any {@link SUPPORT_DASHBOARD_ALLOWED_ROLES} role,
 * regardless of the resolved `organizationType` — a Support/Admin actor whose membership happens
 * to sit under a `PRACTICE`/`CUSTOMER_LAB` organization (e.g. local/dev test data reusing an
 * existing org instead of a dedicated `DELABO_PORTAL` one) must never see another tenant's
 * partner-lab contact data. The org-type check alone is not sufficient in that case. This
 * component only decides its own render output from this — the request itself is already gated
 * the same way, and for every consumer of the shared cache entry, inside {@link
 * useCurrentPartnerLab}.
 */
export function PartnerLabInfo() {
  const { t } = useTranslation('app');
  const tenant = useTenant();
  const principal = usePrincipal();

  const isCustomerOrg = tenant !== null && CUSTOMER_ORG_TYPES.has(tenant.organizationType);
  const isSupportRole = principal !== null && SUPPORT_DASHBOARD_ALLOWED_ROLES.includes(principal.role);
  const isEligible = isCustomerOrg && !isSupportRole;

  // Called unconditionally (rules of hooks) but gated via `enabled` — non-eligible contexts
  // never fire the request at all. The support-role exclusion is enforced again inside the hook
  // itself (shared cache entry), so this `enabled` only needs to cover the org-type check for
  // this component's own render decision; passing `isEligible` here rather than `isCustomerOrg`
  // keeps the two in lockstep without relying on that internal enforcement.
  const { data: partnerLab } = useCurrentPartnerLab({ enabled: isEligible });

  if (!isEligible || partnerLab == null) {
    return null;
  }

  const contactLine = [
    partnerLab.phone != null && partnerLab.phone !== ''
      ? t('portal.header.partnerLab.phone', { phone: partnerLab.phone })
      : null,
    partnerLab.city != null && partnerLab.city !== '' ? partnerLab.city : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <div className="flex items-center gap-3">
      <LaborLogo name={partnerLab.name} logoUrl={partnerLab.logoUrl} />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{partnerLab.name}</span>
        {contactLine !== '' && (
          <span className="text-xs text-muted-foreground">{contactLine}</span>
        )}
      </div>
    </div>
  );
}
